// frontend/src/pages/AdventureMode.tsx
// AI Adventure Mode - çocuklar görev tamamlayarak Python öğrenir

import { useState } from 'react';
import { geminiService, GeminiApiError } from '../services/gemini.service';
import { CodeEditorPanel } from '../components/code-editor/CodeEditorPanel';
import { useFeatures } from '../hooks/useFeatures';
import { usePyodide } from '../hooks/usePyodide';
import '../styles/code-editor.css';

const ADVENTURES = [
  { id: 'mars', title: '🚀 Mars Roketi', theme: 'uzay', description: 'Mars\'a giden roketi kurtar! Her Python görevi seni bir adım ilerletiyor.' },
  { id: 'dino', title: '🦖 Dinozor Parkı', theme: 'dinozorlar', description: 'Dinozor parkı kodlarını yaz ve hayvanları özgür bırak!' },
  { id: 'pirate', title: '🏴‍☠️ Korsan Hazinesi', theme: 'korsanlar', description: 'Gizli hazineyi bulmak için kodları çöz!' },
];

interface Quest { title: string; description: string; starterCode: string; hint: string; }

export function AdventureModePage() {
  const [selected, setSelected] = useState<typeof ADVENTURES[0] | null>(null);
  const [quest, setQuest] = useState<Quest | null>(null);
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { runPython, isLoading: pyodideLoading, isReady } = usePyodide();

  async function startAdventure(adv: typeof ADVENTURES[0]) {
    setSelected(adv);
    setIsLoading(true);
    setQuest(null);
    setResult(null);
    try {
      
      // Quest üretmek için chat kullanıyoruz
      const prompt = `Bir çocuk kodlama oyununda "${adv.title}" macerası için 1 Python görevi üret.
Tema: ${adv.theme}. Görev basit (değişken, döngü veya fonksiyon konularından biri).
SADECE JSON: {"title": "...", "description": "...", "starterCode": "# ...", "hint": "..."}`;
      const questJson = await geminiService.sendChatMessage(prompt, []);
      const q = JSON.parse(questJson.reply.replace(/```json\n?|```/g, '').trim());
      setQuest(q);
      setCode(q.starterCode);
    } catch { setQuest({ title: 'Görev Yükleniyor', description: 'Bir değişken oluştur ve ekrana yazdır!', starterCode: '# Görevini yaz\nisim = "Kahraman"\nprint(isim)', hint: 'print() fonksiyonunu kullan!' }); }
    finally { setIsLoading(false); }
  }

  async function handleRun() {
    if (!isReady || !quest) return;
    const r = await runPython(code);
    setOutput(r.output || r.error || '');
  }

  async function handleSubmit() {
    if (!quest) return;
    setIsLoading(true);
    try {
      const prompt = `Görev: ${quest.description}\nKod:\n${code}\nÇıktı: ${output}\nGörevi başarıyla tamamladı mı? SADECE JSON: {"success": true/false, "message": "kısa hikaye tarzı geri bildirim, eğlenceli Türkçe"}`;
      const res = await geminiService.sendChatMessage(prompt, []);
      const parsed = JSON.parse(res.reply.replace(/```json\n?|```/g, '').trim());
      setResult(parsed.message);
    } catch { setResult('Harika iş! Devam et! 🎉'); }
    finally { setIsLoading(false); }
  }

  if (!selected) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <header className="code-editor-header"><h1>Adventure Mode 🗺️</h1><p>Görev tamamla, Python öğren!</p></header>
        <div className="admin-content-grid">
          {ADVENTURES.map(adv => (
            <div key={adv.id} className="project-card" style={{ cursor: 'pointer' }} onClick={() => startAdventure(adv)}>
              <div className="project-title">{adv.title}</div>
              <div className="project-desc">{adv.description}</div>
              <button className="lesson-btn primary sm" style={{ marginTop: 12 }}>Maceraya Başla →</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="code-editor-page">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <button className="lesson-btn secondary sm" onClick={() => { setSelected(null); setQuest(null); }}>⬅ Maceralar</button>
        <h2 style={{ fontWeight: 800 }}>{selected.title}</h2>
      </div>
      {isLoading && <div className="courses-loading">AI görev hazırlıyor...</div>}
      {quest && !isLoading && (
        <div className="grid grid-2 code-editor-grid">
          <CodeEditorPanel code={code} onChange={setCode} onRun={handleRun} onReset={() => setCode(quest.starterCode)} isRunning={pyodideLoading} />
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 className="parent-section-title">📜 {quest.title}</h3>
              <p style={{ color: 'var(--gray-light)', marginBottom: 12 }}>{quest.description}</p>
              {output && <pre className="output-panel-stdout" style={{ marginBottom: 12 }}>{output}</pre>}
              <details><summary style={{ color: 'var(--gray)', cursor: 'pointer' }}>💡 İpucu</summary><p style={{ color: 'var(--primary-light)', marginTop: 8 }}>{quest.hint}</p></details>
            </div>
            {result ? (
              <div className="card" style={{ borderLeft: '4px solid var(--secondary)' }}>
                <p style={{ fontSize: 16 }}>🎉 {result}</p>
                <button className="lesson-btn primary sm" style={{ marginTop: 12 }} onClick={() => startAdventure(selected)}>Sonraki Görev →</button>
              </div>
            ) : (
              <button className="lesson-btn primary full" onClick={handleSubmit} disabled={isLoading}>🚀 Görevi Tamamla</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
